import { ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { FormBuilder, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subject, Subscription, combineLatest, debounceTime, distinctUntilChanged, filter, first, map, startWith, switchMap, take, tap } from 'rxjs';
import { DevicePriorityEnum, DeviceStatusEnum, ICreateDeviceRequest, IDevice, IDeviceDetailsResponse, IUpdateDeviceRequest } from '../models/device';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import * as DeviceActions from "../../stores/global/devices/device-actions";
import * as DeviceCategoryActions from "../../stores/global/device-categories/actions";
import * as DeviceLocationsActions from "../../stores/global/device-locations/device-locations-actions";
import * as DeviceManufacturerActions from "../../stores/global/device-manufacturers/device-manufacturers-actions";
import * as CompanyActions from "../../stores/global/companies/company-actions";
import { IDeviceCategory } from '../models/device-category';
import { deviceCategoriesSelector } from 'src/app/stores/global/device-categories/selectors';
import { companySelector } from 'src/app/stores/global/companies/company-selectors';
import { deviceLocationSelector } from 'src/app/stores/global/device-locations/device-location-selectors';
import { deviceManufacturerSelector } from 'src/app/stores/global/device-manufacturers/device-manufacturers-selectors';
import { DeviceSerialNumberValidator } from 'src/app/shared/validators/device-serial-number';
import { TranslocoService } from '@jsverse/transloco';
import { DeviceStore } from 'src/app/stores/component/device-store';
import { DeviceCategoryStore } from 'src/app/stores/component/device-category-store';
import { PermissionStorageService } from 'src/app/authentication/storage/permission-storage.service';
import { revisionsSelector } from 'src/app/stores/global/revisions/selectors';
import { DeviceService } from '../services/device.service';
import { DeviceEquipmentValidator } from 'src/app/shared/validators/device-equipment-number.validator';
import { DeviceNoteStore } from 'src/app/stores/component/device-note-store';
import { AttachmentStore } from 'src/app/stores/component/attachment-store';
import { DeviceServiceStore } from 'src/app/stores/component/device-service-store';
import { WearingPartStore } from 'src/app/stores/component/wearing-parts-store';
import { enumToArray } from 'src/app/shared/helpers/enumToArray';

@Component({
    selector: 'app-device-detail',
    templateUrl: './device-detail.component.html',
    styleUrl: './device-detail.component.scss',
    providers: [
        DeviceStore,
        DeviceCategoryStore,
        DeviceNoteStore,
        AttachmentStore,
        DeviceServiceStore,
        WearingPartStore
    ],
    standalone: false
})
export class DeviceDetailComponent {
  isAdmin = false;
  baseUrl: string;
  isNew = false;
  public deletionDialogOpened = false;

  deviceStatus = enumToArray(DeviceStatusEnum, "global.deviceStatusEnums.");
  devicePriority = enumToArray(DevicePriorityEnum, "devicePriorityEnums.");

  companyId$ = new Subject<string>();
  revisionId$ = new Subject<string>();
  deviceIdToDelete$ = new Subject<string>();

  constructor(
    private store: Store<AppStateInterface>,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private deviceService: DeviceService,
    private translocoService: TranslocoService,
    private fb: FormBuilder,
    private deviceStore: DeviceStore,
    private categoryStore: DeviceCategoryStore,
    private permissionServie: PermissionStorageService,
  ) {}

  deviceForm = new FormGroup({
    type: new UntypedFormControl(),
      id : new UntypedFormControl(null),
      deviceManufacturerId: new UntypedFormControl(null, {
        validators: Validators.required,
        updateOn: 'change'
      }),
      companyId: new UntypedFormControl(null, {
        validators: Validators.required,
        updateOn: 'change'
      }),
      serialNumber: new UntypedFormControl(null,{
        // asyncValidators: DeviceSerialNumberValidator(),
        updateOn: 'blur'
      }),
      equipmentNumber: new UntypedFormControl('', {
        asyncValidators: DeviceEquipmentValidator(this.deviceService),
        updateOn: 'blur',
      }),
      tagNumber: new UntypedFormControl(),
      qrNumber: new UntypedFormControl(),
      sequenceNumber: new UntypedFormControl(),
      importantNote: new UntypedFormControl(),
      deviceCategory: new UntypedFormControl(null, {
        validators: Validators.required,
        updateOn: 'change'
      }),
      deviceLocationId: new UntypedFormControl({ 
        value: '', 
        disabled: true}),
      deliveryDate: new UntypedFormControl(null),
      deviceStatus: new UntypedFormControl(1),
      devicePriority: new UntypedFormControl(0),
      maintenanceInterval: new UntypedFormControl(),
  });


  vm$ = combineLatest([ 
    this.deviceStore.device$.pipe(
      tap(x => x?.id == null ? this.isNew = true : this.isNew = false),
      tap(device => this.deviceForm.patchValue({
        ...device,
        companyId: device?.company?.id,
        deviceCategory: device?.deviceCategory,
        deviceManufacturerId: device?.manufacturer?.id,
        deviceLocationId: device?.deviceLocation?.id,
        deliveryDate: device?.deliveryDate ? new Date(device.deliveryDate) : null,
      })),
      tap(device => {
        this.setSerialNumberFieldValidation(device?.deviceCategory)
      })
    ),
    this.store.pipe(select(revisionsSelector)),
    this.store.pipe(select(deviceCategoriesSelector)),
    this.store.pipe(select(deviceLocationSelector)),
    this.store.pipe(select(deviceManufacturerSelector)),
    this.store.pipe(select(companySelector)),
    this.companyId$.pipe(startWith(null)),
    this.revisionId$.pipe(startWith(null)),
    this.deviceIdToDelete$.pipe(startWith(null)),
    this.deviceForm.get('deviceCategory').valueChanges.pipe(
      tap(deviceCategory => {
        this.setSerialNumberFieldValidation(deviceCategory)
      }),
      startWith(null)
    ),
    this.deviceForm.get('deviceManufacturerId').valueChanges.pipe(
      tap(deviceManufacturerId => {
        const value = this.deviceForm.get('serialNumber').value;
        this.deviceForm.get('serialNumber').markAsDirty();
        this.deviceForm.get('serialNumber').patchValue(value, {emitEvent: true})
      }),
      startWith(null)
    ),
    this.deviceStore.device$.pipe( // this pipe is only to load the locations once the device has loaded
      filter(device => device?.company?.id != null),
      take(1),
      tap(device => {
        this.store.dispatch(
        DeviceLocationsActions.getDeviceLocationsByCompany({companyId: device.company.id}),
      );
      if (this.isAdmin) {
        this.deviceForm.get("deviceLocationId").enable();
      }
    }),
      startWith(null)
    )
  ]).pipe(
    map(([
      device,
      revisions,
      deviceCateogries,
      deviceLocations,
      deviceManufacturers,
      companies,
      companyId,
      revisionId,
      deviceIdToDelete
    ]) => ({
      device,
      revisions,
      deviceCateogries,
      deviceLocations,
      deviceManufacturers,
      companies,
      companyId,
      revisionId,
      deviceIdToDelete
    }))
  );

  ngOnInit(): void {
    this.checkPermissions();

    var deviceId = this.route.snapshot.paramMap.get('id');
    if(deviceId != 'new'){
      this.deviceStore.getDeviceById(deviceId);
    }

    this.store.dispatch(DeviceCategoryActions.getDeviceCategories());
    this.store.dispatch(DeviceManufacturerActions.getDeviceManufacturers());

    if (this.isAdmin){
      this.baseUrl = this.router.url.split('/')[0] + '/installations-admin/';
      this.store.dispatch(CompanyActions.getCompanies());
    } else {
      this.baseUrl = this.router.url.split('/')[0] + '/installations/';
    }
  }

  public itemDisabled(itemArgs: { dataItem: any; index: number }) {
    return itemArgs.dataItem.disabled || false; // Disable item if it has 'disabled' set to true
  }

  checkPermissions(): void {
    var permissions = this.permissionServie.getPermissions();
    
    if (permissions && !permissions.includes('Permissions.Devices.SearchGlobal')) {
      this.deviceForm.disable();
      this.isAdmin = false;
    } else {
      this.isAdmin = true;
    }
  }

  setSerialNumberFieldValidation(deviceCategory: IDeviceCategory){
    var formControl = this.deviceForm.get('serialNumber');
    if(deviceCategory?.hasSerialNumber == true){
      formControl.clearAsyncValidators();
      this.deviceForm.get('serialNumber').addAsyncValidators(
        DeviceSerialNumberValidator(this.deviceService));
    }
    else if(formControl != null){
      formControl.clearAsyncValidators();
      formControl?.markAsTouched();
      formControl.setErrors(null);
    }

    formControl.updateValueAndValidity();
  }

  onCompanyChange(event: Event) {
    // reset device location
    this.deviceForm.get('deviceLocationId')?.reset();

    // get device location based on chosen company
    if (event !== null) {
      var id = String(event);
      this.store.dispatch(DeviceLocationsActions.getDeviceLocationsByCompany({companyId: id}));
      if (this.isAdmin) {
        this.deviceForm.get("deviceLocationId").enable();
      }
    } else {
      this.store.dispatch(DeviceLocationsActions.clearDeviceLocations());
      this.deviceForm.get("deviceLocationId").disable();
    }

    // Example triggering change detection manually
    this.cdr.detectChanges();
  }

  saveChanges(deviceId : string, revisionId: string) {
    const formValues = this.deviceForm.value;

    if (deviceId) {
      const updateRequest: IUpdateDeviceRequest = {
        manufacturerId: formValues.deviceManufacturerId,
        type: formValues.type,
        id: deviceId,
        serialNumber: formValues.serialNumber,
        equipmentNumber: formValues.equipmentNumber,
        tagNumber: formValues.tagNumber,
        qrNumber: formValues.qrNumber,
        sequenceNumber: formValues.sequenceNumber,
        importantNote: formValues.importantNote,
        companyId: formValues.companyId,
        deviceCategoryId: formValues.deviceCategory.id,
        deviceLocationId: formValues.deviceLocationId,
        deliveryDate: formValues.deliveryDate,
        deviceStatus: formValues.deviceStatus,
        devicePriority: formValues.devicePriority,
        maintenanceInterval: formValues.maintenanceInterval,
      };

      this.store.dispatch(DeviceActions.updateDevice({ device: updateRequest }));
     
    } else {
      const createRequest: ICreateDeviceRequest = {
        manufacturerId: formValues.deviceManufacturerId,
        type: formValues.type,
        serialNumber: formValues.serialNumber,
        equipmentNumber: formValues.equipmentNumber,
        tagNumber: formValues.tagNumber,
        qrNumber: formValues.qrNumber,
        sequenceNumber: formValues.sequenceNumber,
        importantNote: formValues.importantNote,
        companyId: formValues.companyId,
        deviceCategoryId: formValues.deviceCategory.id,
        deviceLocationId: formValues.deviceLocationId,
        deliveryDate: formValues.deliveryDate,
        deviceStatus: formValues.deviceStatus,
        devicePriority: formValues.devicePriority,
        maintenanceInterval: formValues.maintenanceInterval,
        revisionId: revisionId
      };
      
      this.store.dispatch(DeviceActions.createDevice({ device: createRequest }));
    }
    this.deviceForm.markAsPristine();
  }

  delete(deviceId: string) {
    if(deviceId) {
      this.store.dispatch(DeviceActions.deleteDevice({ id: deviceId }))
    }
  }

  sanitizeCreationDate(inputdate: any) {
    if (!inputdate) {
      return "-";
    }

    const date = new Date(inputdate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  }

  // delete confirmation dialog
  public deletionDialogClose(status: string, deviceId: string): void {
    this.deletionDialogOpened = false;

    if(status == 'delete') {
      this.delete(deviceId);
      this.deviceIdToDelete$.next(null)
    }
  }

  public deletionDialogOpen(deviceId: string): void {
    this.deletionDialogOpened = true;
    this.deviceIdToDelete$.next(deviceId)
  }
}